import * as React from 'react'
import { createContext, useEffect, useMemo, useCallback, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import PropTypes from 'prop-types'
import { fetchShows } from '../components/backend'

export const ReservationContext = createContext()

const HOST = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : ''

export const ReservationContextProvider = ({ children, reservationRetryRef = null }) => {
  const [shows, setShows] = React.useState(() => {
    const cached = sessionStorage.getItem('shows')
    if (cached) {
      const parsed = JSON.parse(cached)
      if (Array.isArray(parsed) && parsed.length > 0) return parsed
    }
    return []
  })
  const [showId, setShowId] = React.useState(-1)
  const [scheduleId, setScheduleId] = React.useState(-1)
  const [enforceSeatGaps, setEnforceSeatGaps] = React.useState(true)
  const [categories, setCategories] = useState([])
  const [selectedSeats, setSelectedSeats] = React.useState([])
  const [firstName, setFirstName] = React.useState([])
  const [lastName, setLastName] = React.useState([])
  const [phone, setPhone] = React.useState([])
  const [email, setEmail] = React.useState([])
  const [sessionUUID, setSessionUUID] = React.useState(null)
  const [expiresAt, setExpiresAt] = React.useState(null)
  const [paymentStatus, setPaymentStatus] = React.useState(null)
  const [paymentData, setPaymentData] = React.useState(null)

  // Restore reservation data from retry bridge when navigating back from a failed payment
  useEffect(() => {
    if (reservationRetryRef?.current) {
      const d = reservationRetryRef.current
      reservationRetryRef.current = null
      setShowId(d.showId)
      setScheduleId(d.scheduleId)
      setCategories(d.categories)
      setSelectedSeats(d.selectedSeats)
      setFirstName(d.firstName)
      setLastName(d.lastName)
      setPhone(d.phone)
      setEmail(d.email)
      setSessionUUID(d.sessionUUID)
      setExpiresAt(d.expiresAt)
    }
  }, [reservationRetryRef])

  const totalTickets = useMemo(() => {
    return categories.reduce((sum, category) => sum + category.quantity, 0)
  }, [categories])
  const totalPrice = useMemo(() => {
    return categories.reduce((sum, category) => sum + category.price * category.quantity * 100, 0)
  }, [categories])

  // Refresh shows when another component (e.g. AdminScheduleManagement) creates a new schedule
  useEffect(() => {
    const handler = () => {
      sessionStorage.removeItem('shows')
      fetchShows()
        .then((data) => {
          setShows(data)
          sessionStorage.setItem('shows', JSON.stringify(data))
        })
        .catch((error) => {
          console.error('Erreur lors du rafraîchissement des spectacles:', error.message)
        })
    }
    window.addEventListener('schedules_refresh', handler)
    return () => window.removeEventListener('schedules_refresh', handler)
  }, [])

  useEffect(() => {
    fetchShows()
      .then((data) => {
        setShows(data)
        sessionStorage.setItem('shows', JSON.stringify(data))
      })
      .catch((error) => {
        console.error('Erreur:', error.message)
      })
  }, [])

  // Sync enforce_seat_gaps when the selected schedule changes
  useEffect(() => {
    if (scheduleId) {
      const current = shows.flatMap((s) => s.schedules || []).find((sc) => sc.id === scheduleId)
      setEnforceSeatGaps(current?.enforce_seat_gaps ?? true)
    } else {
      setEnforceSeatGaps(true)
    }
  }, [scheduleId, shows])

  const resetReservation = useCallback(() => {
    setSelectedSeats([])
    setSessionUUID(null)
    setExpiresAt(null)
  }, [])

  const checkPaymentStatus = useCallback(async (sessionId, sessionUuid) => {
    setPaymentStatus('loading')
    try {
      const res = await fetch(HOST + `/api/stripe/session-status?session_id=${sessionId}&session_uuid=${sessionUuid}`)
      if (res.status >= 500) throw new Error('session-status failed')
      const data = await res.json()
      setPaymentData(data)
      setPaymentStatus(data.status)
    } catch {
      setPaymentStatus('error')
    }
  }, [])

  const getClientSecret = useCallback(
    async (setErrorMessage) => {
      try {
        const response = await fetch(HOST + '/api/stripe/create-checkout-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            showId,
            scheduleId,
            categories: categories
              .filter((category) => category.quantity > 0)
              .map(({ id, quantity, price, name }) => ({ id, quantity, price, name })),
            selectedSeats: selectedSeats.map(({ id, row, seat }) => ({ id, row, seat })),
            firstName,
            lastName,
            phone,
            email,
            sessionUUID
          })
        })
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error?.message || 'Erreur lors de la création de la session')
        }

        const data = await response.json()
        return data.clientSecret
      } catch (error) {
        console.error('Erreur:', error.message)
        // Afficher l'erreur à l'utilisateur
        setErrorMessage(error.message)
        return null
      }
    },
    [showId, scheduleId, categories, selectedSeats, firstName, lastName, phone, email, sessionUUID]
  )
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)

  return (
    <ReservationContext.Provider
      value={{
        shows,
        showId,
        setShowId,
        scheduleId,
        setScheduleId,
        enforceSeatGaps,
        setEnforceSeatGaps,
        categories,
        setCategories,
        totalTickets,
        totalPrice,
        selectedSeats,
        setSelectedSeats,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        phone,
        setPhone,
        email,
        setEmail,
        sessionUUID,
        setSessionUUID,
        expiresAt,
        setExpiresAt,
        resetReservation,
        stripePromise,
        getClientSecret,
        paymentStatus,
        paymentData,
        checkPaymentStatus,
        reservationRetryRef
      }}
    >
      {children}
    </ReservationContext.Provider>
  )
}

ReservationContextProvider.propTypes = {
  children: PropTypes.node,
  reservationRetryRef: PropTypes.object
}
